'use client';

import { type ReactNode, useCallback, useRef } from 'react';

import { cn } from '@ps/ui/utils';
import { Play } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import {
  useIsVideoPaused,
  useToggleOverlay,
  useTogglePause,
} from '../contexts/video-player';

type VideoTouchBehaviorProps = {
  children: ReactNode;
  className?: string;
  /** Threshold (0-1) for where tap toggles overlay vs pause. Default 0.4. */
  threshold?: number;
  /** When true, tapping the upper area no longer toggles the overlay. */
  disableOverlayToggle?: boolean;
};

export function VideoTouchBehavior({
  children,
  className,
  threshold = 0.4,
  disableOverlayToggle,
}: VideoTouchBehaviorProps) {
  const paused = useIsVideoPaused();
  const togglePause = useTogglePause();
  const toggleOverlay = useToggleOverlay();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const relativeY = e.clientY - rect.top;
      if (relativeY > rect.height * threshold) {
        togglePause();
      } else if (!disableOverlayToggle) {
        toggleOverlay();
      }
    },
    [threshold, togglePause, toggleOverlay, disableOverlayToggle],
  );

  return (
    <div
      ref={containerRef}
      className={cn('relative flex-1', className)}
      onClick={handleClick}
    >
      {children}

      {/* Play icon overlay when paused */}
      <AnimatePresence>
        {paused && (
          <motion.div
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.15 }}
          >
            <Play
              className="fill-raise/50 text-raise/50 h-14 w-14"
              strokeWidth={0}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
